class BlogUpdatedSort extends HTMLElement {
  connectedCallback() {
    this.articleList = this.querySelector("article-list");
    this.pagination = this.querySelector(".pagination");
    this.pageCount = Number(this.dataset.pageCount);
    this.pageSize = Number(this.dataset.pageSize);
    this.abortController = new AbortController();
    this.fetchController = new AbortController();

    if (!this.articleList || !this.pageSize || !this.pageCount) {
      return;
    }

    const signal = this.abortController.signal;
    this.addEventListener("click", this.onPaginationClick.bind(this), { signal });
    window.addEventListener("popstate", this.onPopState.bind(this), { signal });
    this.loadArticles();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.fetchController?.abort();
  }

  getCurrentPage() {
    const pageFromUrl = Number(new URL(window.location.href).searchParams.get("page"));
    return Math.min(Math.max(pageFromUrl || Number(this.dataset.currentPage) || 1, 1), this.pageCount);
  }

  createRecords(elements, page) {
    return elements.map((element, index) => ({
      element,
      position: (page - 1) * this.pageSize + index,
      updatedAt: Number(element.dataset.updatedAt) || 0,
    }));
  }

  sortRecords(records) {
    return records.sort((first, second) => {
      return second.updatedAt - first.updatedAt || first.position - second.position;
    });
  }

  async loadArticles() {
    const currentPage = this.getCurrentPage();
    const initialElements = Array.from(this.articleList.querySelectorAll(".article-item"));
    for (const element of initialElements) {
      element.removeAttribute("reveal");
      element.removeAttribute("reveal-visibility");
    }

    const initialRecords = this.createRecords(initialElements, currentPage);
    this.records = this.sortRecords([...initialRecords]);
    this.articleList.replaceChildren(...this.records.map(({ element }) => element));

    try {
      const pages = Array.from({ length: this.pageCount }, (_, index) => index + 1)
        .filter((page) => page !== currentPage);
      let nextPageIndex = 0;

      const workers = Array.from({ length: Math.min(4, pages.length) }, async () => {
        while (nextPageIndex < pages.length) {
          const page = pages[nextPageIndex++];
          const response = await fetch(this.getPageUrl(page), {
            credentials: "same-origin",
            signal: this.fetchController.signal,
          });

          if (!response.ok) {
            throw new Error(`Unable to load blog page ${page}: ${response.status}`);
          }

          const html = await response.text();
          const documentForPage = new DOMParser().parseFromString(html, "text/html");
          const elements = Array.from(documentForPage.querySelectorAll("blog-updated-sort .article-item"));

          if (elements.length === 0 && initialElements.length > 0) {
            throw new Error(`No blog articles found on page ${page}`);
          }

          this.records.push(...this.createRecords(elements, page));
        }
      });

      await Promise.all(workers);
      if (!this.isConnected) {
        return;
      }

      this.records = this.sortRecords(this.records);
      this.renderPage(currentPage);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn("Blog updated-date sorting could not load every page.", error);
        this.fetchController.abort();
        this.records = this.sortRecords(initialRecords);
        this.articleList.replaceChildren(...this.records.map(({ element }) => element));
        this.articleList.removeAttribute("aria-busy");
      }
    }
  }

  getPageUrl(page) {
    const url = new URL(window.location.href);
    if (page === 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", page);
    }
    return url.toString();
  }

  onPaginationClick(event) {
    const link = event.target.closest("a[data-page]");
    if (!link) {
      return;
    }

    event.preventDefault();
    this.renderPage(Number(link.dataset.page));
    window.history.pushState({}, "", this.getPageUrl(Number(link.dataset.page)));
  }

  onPopState() {
    this.renderPage(this.getCurrentPage());
  }

  renderPage(page) {
    const currentPage = Math.min(Math.max(page, 1), this.pageCount);
    const start = (currentPage - 1) * this.pageSize;
    const pageRecords = this.records.slice(start, start + this.pageSize);

    for (const { element } of pageRecords) {
      element.removeAttribute("reveal");
      element.removeAttribute("reveal-visibility");
    }

    this.articleList.replaceChildren(...pageRecords.map(({ element }) => element));
    this.articleList.removeAttribute("aria-busy");
    this.dataset.currentPage = currentPage;
    this.renderPagination(currentPage);
  }

  renderPagination(currentPage) {
    if (!this.pagination) {
      return;
    }

    const nav = this.pagination.querySelector("nav");
    if (!nav) {
      return;
    }

    nav.replaceChildren();
    const makeArrow = (page, direction, label) => {
      const link = document.createElement("a");
      link.className = "pagination__nav-item";
      link.setAttribute("rel", direction);
      link.setAttribute("aria-label", label);
      link.dataset.page = page;
      link.href = this.getPageUrl(page);
      link.textContent = direction === "prev" ? "\u2039" : "\u203a";
      return link;
    };

    if (currentPage > 1) {
      nav.append(makeArrow(currentPage - 1, "prev", this.dataset.labelPrevious));
    }

    const visiblePages = new Set([1, this.pageCount]);
    for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
      if (page > 1 && page < this.pageCount) {
        visiblePages.add(page);
      }
    }

    let previousPage = 0;
    for (const page of [...visiblePages].sort((first, second) => first - second)) {
      if (previousPage && page - previousPage > 1) {
        const ellipsis = document.createElement("span");
        ellipsis.className = "pagination__nav-item";
        ellipsis.setAttribute("aria-hidden", "true");
        ellipsis.textContent = "\u2026";
        nav.append(ellipsis);
      }

      if (page === currentPage) {
        const current = document.createElement("span");
        current.className = "pagination__nav-item heading heading--small";
        current.setAttribute("aria-current", "page");
        current.textContent = page;
        nav.append(current);
      } else {
        const link = document.createElement("a");
        link.className = "pagination__nav-item heading heading--small";
        link.dataset.page = page;
        link.href = this.getPageUrl(page);
        link.setAttribute(
          "aria-label",
          this.dataset.labelPageTemplate.replace("__PAGE__", page),
        );
        link.textContent = page;
        nav.append(link);
      }

      previousPage = page;
    }

    if (currentPage < this.pageCount) {
      nav.append(makeArrow(currentPage + 1, "next", this.dataset.labelNext));
    }
  }
}

if (!customElements.get("blog-updated-sort")) {
  customElements.define("blog-updated-sort", BlogUpdatedSort);
}